import React, { useState, useEffect } from 'react';
import pin from '../../Pages/Worldmap/SVG/pin.svg';
import shoppingIcon from '../../Pages/Worldmap/SVG/2_SHOPPING.png';
import hotelIcon from '../../Pages/Worldmap/SVG/3_SONETTE.png';
import officeIcon from '../../Pages/Worldmap/SVG/8_OFFICE_STROKE.png';
import restaurantIcon from '../../Pages/Worldmap/SVG/5_CUISINE.png';
import culturalIcon from '../../Pages/Worldmap/SVG/6_PEINTURE.png';
import residenceIcon from '../../Pages/Worldmap/SVG/7_SERRURE.png';
import urbanIcon from '../../Pages/Worldmap/SVG/8_BANC.png';
import architectureIcon from '../../Pages/Worldmap/SVG/4_OFFICE.png';
import installationIcon from '../../Pages/Worldmap/SVG/9_INSTALLATION_SCENOGRAPHY.png';
import cityLogo from '../../Pages/Worldmap/SVG/city_horizontal.png';
import ouiDesignLogo from '../../Pages/Worldmap/SVG/oui_design_horizontal.webp';

const POINT_ICONS = {
    "Retail": shoppingIcon,
    "Hotel": hotelIcon,
    "Office": officeIcon,
    "Restaurant/bar": restaurantIcon,
    "Cultural space": culturalIcon,
    "Private residence": residenceIcon,
    "Urban space/Transportation": urbanIcon,
    "Architecture": architectureIcon,
    "Installation/Scenography": installationIcon
};

const EmbeddedMapViewer = ({ item, type, onClose }) => {
    const [itemId, setItemId] = useState(null);
    
    // Store item data in sessionStorage when component mounts
    useEffect(() => {
        const id = `admin_map_item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem(id, JSON.stringify({...item, type}));
        setItemId(id);
        
        // Cleanup on unmount
        return () => {
            sessionStorage.removeItem(id);
        };
    }, [item, type]);

    const getItemImage = () => {
        if (type === 'event') {
            if (item.design_events === 'In the City') return cityLogo;
            if (item.design_events === 'Oui Design! 2025') return ouiDesignLogo;
            return null;
        }
        return item.photos_projet?.split(',')[0].trim();
    };

    const getItemTitle = () => {
        return item.titre_affiche || item.nom_etablissement || 'Sans Titre';
    };

    const getItemCategory = () => {
        if (type === 'event') {
            return item.design_events || item.event_type;
        }
        if (type === 'pos') {
            return item.design_points_of_sales;
        }
        return item.points_of_interest;
    };

    const getItemDescription = () => {
        return item.description_projet || item.description_agence_fr || '';
    };

    const formatDate = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getEventDates = () => {
        if (type !== 'event') return null;
        const dates = [];
        if (item.date_debut_expo) dates.push(`Début: ${formatDate(item.date_debut_expo)}`);
        if (item.date_fin_expo) dates.push(`Fin: ${formatDate(item.date_fin_expo)}`);
        if (item.date_vernissage) dates.push(`Vernissage: ${formatDate(item.date_vernissage)}`);
        return dates.length > 0 ? dates : null;
    };

    const styles = {
        container: {
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'white',
            border: '2px solid #e9ecef',
            borderRadius: '16px',
            marginBottom: '30px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            animation: 'slideDown 0.4s ease-out'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 30px',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef'
        },
        headerTitle: {
            fontSize: '20px',
            fontWeight: '700',
            color: '#212529',
            margin: 0
        },
        closeButton: {
            background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            minWidth: '36px',
            minHeight: '36px',
            cursor: 'pointer',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)',
            flexShrink: 0,
            lineHeight: 1,
            padding: 0
        },
        content: {
            display: 'flex',
            height: '500px'
        },
        infoPanel: {
            flex: '0 0 350px',
            backgroundColor: '#f9f9f9',
            borderRight: '1px solid #e9ecef',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        },
        mapPanel: {
            flex: 1,
            position: 'relative',
            overflow: 'hidden'
        },
        itemImage: {
            width: '100%',
            height: '200px',
            objectFit: 'cover',
            borderBottom: '1px solid #e9ecef'
        },
        eventLogo: {
            width: '100%',
            height: '120px',
            objectFit: 'contain',
            backgroundColor: 'white',
            padding: '20px',
            borderBottom: '1px solid #e9ecef'
        },
        infoSection: {
            padding: '20px',
            flex: 1,
            overflow: 'auto'
        },
        categorySection: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '15px',
            padding: '10px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
        },
        categoryIcon: {
            width: '24px',
            height: '24px',
            objectFit: 'contain'
        },
        categoryText: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#495057',
            textTransform: 'uppercase',
            margin: 0
        },
        itemTitle: {
            fontSize: '18px',
            fontWeight: '700',
            color: '#0039d6',
            marginBottom: '10px',
            textDecoration: 'none'
        },
        agencyTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#0039d6',
            marginBottom: '8px',
            textDecoration: 'none'
        },
        description: {
            fontSize: '14px',
            color: '#495057',
            lineHeight: '1.6',
            marginBottom: '15px',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        year: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#212529',
            marginBottom: '10px'
        },
        addressSection: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '15px',
            padding: '10px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
        },
        addressText: {
            fontSize: '14px',
            color: '#495057',
            margin: 0
        },
        datesList: {
            marginBottom: '15px'
        },
        dateItem: {
            fontSize: '13px',
            color: '#6c757d',
            marginBottom: '5px',
            padding: '5px 10px',
            backgroundColor: 'white',
            borderRadius: '6px',
            border: '1px solid #e9ecef'
        },
        iframe: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none'
        }
    };

    // Add keyframes for animation
    if (!document.getElementById('embedded-map-animations')) {
        const animationStyles = document.createElement('style');
        animationStyles.id = 'embedded-map-animations';
        animationStyles.textContent = `
            @keyframes slideDown {
                from { 
                    opacity: 0; 
                    transform: translateY(-30px); 
                }
                to { 
                    opacity: 1; 
                    transform: translateY(0); 
                }
            }
        `;
        document.head.appendChild(animationStyles);
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h3 style={styles.headerTitle}>
                    🌍 Vue sur la Carte - {getItemTitle()}
                </h3>
                <button
                    style={styles.closeButton}
                    onClick={onClose}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(220, 53, 69, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 53, 69, 0.3)';
                    }}
                    title="Fermer la vue carte"
                >
                    ✕
                </button>
            </div>
            
            <div style={styles.content}>
                {/* Info Panel */}
                <div style={styles.infoPanel}>
                    {/* Image/Logo */}
                    {getItemImage() && (
                        type === 'event' ? (
                            <img 
                                src={getItemImage()} 
                                alt={getItemCategory()} 
                                style={styles.eventLogo}
                            />
                        ) : (
                            <img 
                                src={getItemImage()} 
                                alt={getItemTitle()} 
                                style={styles.itemImage}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        )
                    )}
                    
                    <div style={styles.infoSection}>
                        {/* Category */}
                        {getItemCategory() && (
                            <div style={styles.categorySection}>
                                {POINT_ICONS[getItemCategory()] && (
                                    <img 
                                        src={POINT_ICONS[getItemCategory()]} 
                                        alt={getItemCategory()}
                                        style={styles.categoryIcon}
                                    />
                                )}
                                <p style={styles.categoryText}>{getItemCategory()}</p>
                            </div>
                        )}

                        {/* Agency Title (for projects) */}
                        {item.fiche_agence && type === 'project' && (
                            <div style={styles.agencyTitle}>
                                🏢 {item.fiche_agence}
                            </div>
                        )}

                        {/* Item Title */}
                        <div style={styles.itemTitle}>
                            {type === 'project' && '📍'}
                            {type === 'pos' && '🏪'}
                            {type === 'event' && '🎨'}
                            {' '}{getItemTitle()}
                        </div>

                        {/* Year */}
                        {item.annee_projet && (
                            <div style={styles.year}>📅 {item.annee_projet}</div>
                        )}

                        {/* Description */}
                        {getItemDescription() && (
                            <div style={styles.description}>
                                {getItemDescription()}
                            </div>
                        )}

                        {/* Event Dates */}
                        {getEventDates() && (
                            <div style={styles.datesList}>
                                {getEventDates().map((date, index) => (
                                    <div key={index} style={styles.dateItem}>
                                        📅 {date}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Address */}
                        {item.adresse && (
                            <div style={styles.addressSection}>
                                <img src={pin} alt="Location" style={{ width: '16px', height: '16px' }} />
                                <p style={styles.addressText}>{item.adresse}</p>
                            </div>
                        )}

                        {/* Links for events */}
                        {type === 'event' && (item.site_web || item.instagram || item.rsvp) && (
                            <div style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {item.site_web && (
                                    <a 
                                        href={item.site_web} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{
                                            color: '#0039d6',
                                            textDecoration: 'none',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            padding: '5px 10px',
                                            backgroundColor: 'white',
                                            borderRadius: '6px',
                                            border: '1px solid #e9ecef'
                                        }}
                                    >
                                        🌐 Site Web
                                    </a>
                                )}
                                {item.instagram && (
                                    <div style={{ 
                                        fontSize: '13px', 
                                        color: '#6c757d',
                                        padding: '5px 10px',
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        border: '1px solid #e9ecef'
                                    }}>
                                        📸 {item.instagram}
                                    </div>
                                )}
                                {item.rsvp && (
                                    <a 
                                        href={item.rsvp} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{
                                            color: '#28a745',
                                            textDecoration: 'none',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            padding: '5px 10px',
                                            backgroundColor: 'white',
                                            borderRadius: '6px',
                                            border: '1px solid #e9ecef'
                                        }}
                                    >
                                        📝 RSVP
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Map Panel */}
                <div 
                    style={styles.mapPanel}
                >
                    {itemId ? (
                        <iframe
                            src={`/worldmap-admin-view?itemId=${itemId}`}
                            title="Carte Interactive"
                            style={styles.iframe}
                            allow="geolocation"
                        />
                    ) : (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            padding: '20px',
                            borderRadius: '10px',
                            textAlign: 'center',
                            zIndex: 20
                        }}>
                            <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
                            <div>Initialisation de la carte...</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmbeddedMapViewer;